import { useEffect, useState } from 'react';
import { Eye, X } from 'lucide-react';
import { adminUsersApi } from '../api/adminUsersApi';
import { Button, Modal, SectionHeading } from '../components/ui';
import type { DeletedAccountArchive, DeletedAccountArchiveSummary } from '../types/adminUsers';
import { c } from '../styles/theme';

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unable to load the deletion archive.';
}

export default function AdminArchive() {
  const [items, setItems] = useState<DeletedAccountArchiveSummary[]>([]);
  const [selected, setSelected] = useState<DeletedAccountArchive | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    void adminUsersApi
      .deletedAccounts()
      .then((result) => {
        if (active) setItems(result);
      })
      .catch((cause) => {
        if (active) setError(errorMessage(cause));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  async function inspect(id: string) {
    setDetailLoading(true);
    setError('');
    try {
      setSelected(await adminUsersApi.deletedAccount(id));
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setDetailLoading(false);
    }
  }

  return (
    <section>
      <SectionHeading
        as="h2"
        title="Deleted account archive"
        subtitle="Read-only records retained for manual inspection. Deleted accounts cannot be restored."
        marginBottom={16}
      />
      {error && (
        <p role="alert" style={{ color: c.danger, fontSize: 12 }}>
          {error}
        </p>
      )}
      {loading ? (
        <p style={{ color: c.text2, fontSize: 13 }}>Loading archive...</p>
      ) : items.length === 0 ? (
        <p style={{ color: c.text2, fontSize: 13 }}>No deleted accounts have been archived.</p>
      ) : (
        <div style={{ overflowX: 'auto', borderTop: `1px solid ${c.border}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ color: c.text2, textAlign: 'left' }}>
                {['Account', 'Deleted', 'Source', 'Audio purge date', ''].map((label) => (
                  <th key={label} style={{ padding: '10px 8px', fontWeight: 500 }}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} style={{ borderTop: `1px solid ${c.border}` }}>
                  <td style={{ padding: '10px 8px' }}>
                    <div style={{ color: c.text1 }}>{item.name}</div>
                    <div style={{ color: c.text3 }}>{item.email}</div>
                  </td>
                  <td style={{ padding: '10px 8px', color: c.text2 }}>
                    {new Date(item.deletedAt).toLocaleString()}
                  </td>
                  <td style={{ padding: '10px 8px', color: c.text2 }}>{item.source}</td>
                  <td style={{ padding: '10px 8px', color: c.text2 }}>
                    {item.audioObjectCount === 0
                      ? 'No audio'
                      : new Date(item.audioDeleteAfter).toLocaleString()}
                  </td>
                  <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                    <Button
                      variant="secondary"
                      disabled={detailLoading}
                      onClick={() => void inspect(item.id)}
                      aria-label={`Inspect archive for ${item.email}`}
                    >
                      <Eye size={14} /> Inspect
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={selected !== null}
        title="Archived account record"
        onClose={() => setSelected(null)}
        headerAction={
          <Button
            variant="ghost"
            onClick={() => setSelected(null)}
            aria-label="Close archive record"
          >
            <X size={16} />
          </Button>
        }
      >
        {selected && (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(100px, 140px) 1fr',
                gap: 8,
                fontSize: 12,
              }}
            >
              <span style={{ color: c.text3 }}>Account</span>
              <span>
                {selected.name} ({selected.email})
              </span>
              <span style={{ color: c.text3 }}>Deleted</span>
              <span>{new Date(selected.deletedAt).toLocaleString()}</span>
              <span style={{ color: c.text3 }}>Source</span>
              <span>{selected.source}</span>
              <span style={{ color: c.text3 }}>Reason</span>
              <span>{selected.reason ?? 'Not supplied'}</span>
              <span style={{ color: c.text3 }}>Retained audio</span>
              <span>{selected.audioObjectCount} objects</span>
              <span style={{ color: c.text3 }}>Audio purge date</span>
              <span>{new Date(selected.audioDeleteAfter).toLocaleString()}</span>
            </div>
            <pre
              style={{
                marginTop: 16,
                maxHeight: '50vh',
                overflow: 'auto',
                padding: 12,
                border: `1px solid ${c.border}`,
                borderRadius: 6,
                background: c.surface1,
                color: c.text2,
                fontSize: 11,
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
              }}
            >
              {JSON.stringify(selected.snapshot, null, 2)}
            </pre>
          </>
        )}
      </Modal>
    </section>
  );
}
